// ============================================
// MEGAPANELDESKTOP.JS — Panel mega menú horizontal estilo Syscom
// ============================================
// 3 columnas: N1 → N2 → N3. Hover para previsualizar, click filtra.

import React, { useEffect, useState } from 'react';
import { API_BASE_CLI, PP } from './helpersClientes';
import { LayoutGrid, FolderOpen } from 'lucide-react';
import { IconoCategoria } from '../utils/iconosCategorias';
import { tokens } from '../styles/tokens';
import { authHeaders } from '../utils/storage';

export default function MegaPanelDesktop({ open, onSeleccionar, onCerrar }) {
  const [arbol, setArbol] = useState({ beep: null, syscom: [] });
  const [cargando, setCargando] = useState(true);
  const [hoverN1, setHoverN1] = useState(null);
  const [hoverN2, setHoverN2] = useState(null);

  useEffect(() => {
    if (!open) return;
    let activo = true;
    setCargando(true);
    Promise.all([
      fetch(`${API_BASE_CLI}/api/tienda/categorias-arbol`, { headers: authHeaders() }).then(x => x.json()).catch(() => null),
      fetch(`${API_BASE_CLI}/api/syscom/categorias-arbol`, { headers: authHeaders() }).then(x => x.json()).catch(() => null),
    ]).then(([beepRes, syscomRes]) => {
      if (!activo) return;
      const beep = beepRes?.ok && Array.isArray(beepRes.categorias)
        ? beepRes.categorias.find(c => c.id === 'beep')
        : null;
      const syscom = syscomRes?.ok && Array.isArray(syscomRes.arbol)
        ? syscomRes.arbol.map(normalizarSyscom)
        : [];
      setArbol({ beep, syscom });
    }).finally(() => { if (activo) setCargando(false); });
    return () => { activo = false; };
  }, [open]);

  // ESC cierra
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onCerrar && onCerrar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCerrar]);

  // Reset hover al cerrar
  useEffect(() => {
    if (!open) {
      setHoverN1(null);
      setHoverN2(null);
    }
  }, [open]);

  if (!open) return null;

  const lista1 = [];
  if (arbol.beep) lista1.push(arbol.beep);
  lista1.push(...(arbol.syscom || []));

  const lista2 = hoverN1 ? (Array.isArray(hoverN1.children) ? hoverN1.children : []) : [];
  const lista3 = hoverN2 ? (Array.isArray(hoverN2.children) ? hoverN2.children : []) : [];

  const seleccionar = (cat) => {
    onSeleccionar && onSeleccionar(cat ? cat.id : null);
    onCerrar && onCerrar();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onCerrar}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', zIndex: 90,
        }}
      />
      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          background: tokens.surface,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          borderRadius: '0 0 12px 12px',
          zIndex: 100,
          fontFamily: PP, color: tokens.text,
          maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
          animation: 'megaSlideDown 0.2s ease',
        }}
      >
        {/* Header */}
        <div style={{
          flexShrink: 0,
          padding: '14px 24px', borderBottom: `1px solid ${tokens.borderSubtle}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: tokens.primary }}>
            <LayoutGrid size={18} strokeWidth={2} /> Categorías
          </div>
          <button
            onClick={onCerrar}
            aria-label="Cerrar"
            style={{
              background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer',
              color: tokens.textMuted, padding: '4px 8px',
            }}
          >×</button>
        </div>

        {/* Cuerpo: 3 columnas */}
        <div style={{
          flex: '1 1 auto', minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 280px) minmax(220px, 280px) 1fr',
        }}>
          {/* COLUMNA 1 */}
          <div style={{
            overflowY: 'auto', borderRight: `1px solid ${tokens.borderSubtle}`,
            padding: '10px 0',
          }}>
            <ItemSelectable
              nombre="⭐ Todos los productos"
              onClick={() => seleccionar(null)}
            />
            {cargando && (
              <div style={{ padding: 16, color: tokens.textMuted, fontSize: 13 }}>
                Cargando…
              </div>
            )}
            {!cargando && lista1.length === 0 && (
              <div style={{ padding: 16, color: tokens.textMuted, fontSize: 13, lineHeight: 1.5 }}>
                Categorías no sincronizadas. Pide al admin que sincronice
                el árbol desde el <strong>panel de administración</strong>.
              </div>
            )}
            {lista1.map(cat => (
              <ItemMega
                key={cat.id}
                nombre={cat.nombre}
                icono={true}
                imagenUrl={cat.imagen_url}
                contador={cat.contador}
                activa={hoverN1?.id === cat.id}
                tieneFlecha={Array.isArray(cat.children) && cat.children.length > 0}
                onMouseEnter={() => {
                  setHoverN1(cat);
                  setHoverN2(null);
                }}
                onClick={() => seleccionar(cat)}
              />
            ))}
          </div>

          {/* COLUMNA 2 */}
          <div style={{
            overflowY: 'auto', borderRight: `1px solid ${tokens.borderSubtle}`,
            padding: '10px 0', background: tokens.surfaceSubtle,
          }}>
            {hoverN1 && (
              <ItemSelectable
                nombre={`Ver todo en ${hoverN1.nombre}`}
                onClick={() => seleccionar(hoverN1)}
                destacado
              />
            )}
            {!hoverN1 && (
              <div style={{ padding: 16, color: tokens.textMuted, fontSize: 13 }}>
                Pasa el cursor sobre una categoría →
              </div>
            )}
            {hoverN1 && lista2.length === 0 && (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 8, padding: '48px 16px', textAlign: 'center',
              }}>
                <FolderOpen size={28} color={tokens.textMuted} strokeWidth={2} />
                <div style={{ color: tokens.textMuted, fontSize: 13 }}>Sin subcategorías</div>
              </div>
            )}
            {lista2.map(cat => (
              <ItemMega
                key={cat.id}
                nombre={cat.nombre}
                icono={(cat.icono && cat.icono !== '📂' && cat.icono !== '📦') ? cat.icono : null}
                contador={cat.contador}
                activa={hoverN2?.id === cat.id}
                tieneFlecha={Array.isArray(cat.children) && cat.children.length > 0}
                onMouseEnter={() => setHoverN2(cat)}
                onClick={() => seleccionar(cat)}
              />
            ))}
          </div>

          {/* COLUMNA 3 */}
          <div style={{ overflowY: 'auto', padding: '10px 0' }}>
            {hoverN2 && (
              <ItemSelectable
                nombre={`Ver todo en ${hoverN2.nombre}`}
                onClick={() => seleccionar(hoverN2)}
                destacado
              />
            )}
            {!hoverN2 && hoverN1 && (
              <div style={{ padding: 16, color: tokens.textMuted, fontSize: 13 }}>
                Pasa el cursor sobre una subcategoría →
              </div>
            )}
            {hoverN2 && lista3.length === 0 && (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 8, padding: '48px 16px', textAlign: 'center',
              }}>
                <FolderOpen size={28} color={tokens.textMuted} strokeWidth={2} />
                <div style={{ color: tokens.textMuted, fontSize: 13 }}>Sin subniveles</div>
              </div>
            )}
            {lista3.map(cat => (
              <ItemMega
                key={cat.id}
                nombre={cat.nombre}
                contador={cat.contador}
                onClick={() => seleccionar(cat)}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes megaSlideDown {
          from { transform: translateY(-12px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}

function normalizarSyscom(n) {
  return {
    id: `syscom_${n.id}`,
    raw_id: n.id,
    nombre: n.nombre,
    icono: null,
    imagen_url: n.imagen_url || null,
    contador: n.productos_count || 0,
    children: Array.isArray(n.children) ? n.children.map(normalizarSyscom) : [],
  };
}

function ItemSelectable({ nombre, onClick, destacado }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '10px 16px', cursor: 'pointer',
        fontSize: 13, fontWeight: 700,
        color: tokens.accent,
        background: destacado ? tokens.primarySoft : 'transparent',
        borderBottom: `1px solid ${tokens.borderSubtle}`,
        marginBottom: 4,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = tokens.primarySoft; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = destacado ? tokens.primarySoft : 'transparent'; }}
    >{nombre}</div>
  );
}

function ItemMega({ nombre, icono, imagenUrl, contador, activa, onClick, onMouseEnter, tieneFlecha }) {
  return (
    <div
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        padding: '10px 16px', cursor: 'pointer',
        fontSize: 14,
        background: activa ? tokens.primarySoft : 'transparent',
        color: activa ? tokens.accent : tokens.primary,
        fontWeight: activa ? 600 : 500,
        transition: 'background 0.1s',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
        {imagenUrl ? (
          <img src={imagenUrl} alt="" loading="lazy" decoding="async"
            style={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (icono && <IconoCategoria nombre={nombre} size={20} />)}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {nombre}
        </span>
        {contador != null && contador > 0 && (
          <span style={{ color: tokens.textMuted, fontSize: 11, fontWeight: 500 }}>
            ({contador})
          </span>
        )}
      </span>
      {tieneFlecha && <span style={{ color: tokens.textMuted }}>›</span>}
    </div>
  );
}
